import { SITE_ORIGIN } from './constants.js'
import { blogPostsMeta } from '../data/blogPostsMeta.js'
import { solutionsFaq } from '../data/solutionsContent.js'
import { faqPageFaq } from '../data/faqContent.js'

/**
 * Site-wide + route-specific schema.org JSON-LD graph.
 * Single source of truth for both the client (src/App.jsx, on SPA nav) and
 * the build-time prerender (scripts/prerender.mjs, in the initial HTML).
 *
 * Kept intentionally to types that are valid AND accurate for a SaaS platform:
 * Organization + WebSite site-wide, BlogPosting/BreadcrumbList on posts, FAQPage
 * on /faq. We deliberately do NOT emit Product/SoftwareApplication/Offer (they
 * require aggregateRating/review to validate, and we won't fabricate reviews) or
 * LocalBusiness/RealEstateAgent (Akara is software, not a storefront/agent).
 *
 * @param {string} pathname - location.pathname
 * @param {string} [origin] - absolute origin for URLs
 * @returns {object[]} schema.org items, or [] for non-public routes
 */
export function buildStructuredData(pathname, origin = SITE_ORIGIN) {
  const isPublic =
    !pathname.startsWith('/dashboard') &&
    !pathname.startsWith('/admin') &&
    !pathname.startsWith('/deposit') &&
    !['/login', '/reset-password'].includes(pathname)

  if (!isPublic) return []

  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Akara',
    url: origin,
    logo: `${origin}/favicon.png`,
    description: 'Security deposit management software for rental and service businesses in the UAE.',
    areaServed: 'AE',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@akara.ae',
      contactType: 'customer support',
      areaServed: 'AE',
    },
  }

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Akara',
    url: origin,
    publisher: { '@type': 'Organization', name: 'Akara' },
  }

  const items = [organization, website]

  if (pathname.startsWith('/blog/')) {
    const slug = pathname.slice('/blog/'.length).replace(/\/$/, '')
    const post = blogPostsMeta.find((p) => p.slug === slug)
    if (post) {
      items.push({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.description,
        datePublished: post.date,
        dateModified: post.date,
        articleSection: post.category,
        author: { '@type': 'Organization', name: 'Akara', url: origin },
        publisher: {
          '@type': 'Organization',
          name: 'Akara',
          logo: { '@type': 'ImageObject', url: `${origin}/favicon.png` },
        },
        image: `${origin}/og-cover.png`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${origin}/blog/${slug}/` },
      })
      items.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${origin}/blog/` },
          { '@type': 'ListItem', position: 3, name: post.title, item: `${origin}/blog/${slug}/` },
        ],
      })
    }
  }

  const solutionFaq = solutionsFaq[pathname.replace(/\/$/, '')]
  if (solutionFaq) {
    items.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: solutionFaq.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    })
  }

  if (pathname.startsWith('/faq')) {
    items.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqPageFaq.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    })
  }

  return items
}
